import axios from "axios";
import axiosRetry from "axios-retry";
import config from "config";
import { spinnerService } from "components/Spinner";
import { history } from "./helpers";
import { TIMEOUT_LIMIT } from "./constants";

class Api {
  constructor() {
    this.API = axios.create({
      baseURL: config.api
    });
    axiosRetry(this.API, { retries: 3 });
    this.PAANS_API = axios.create({
      baseURL: config.paans.checkAPILink
    });
    this.httpMethods = {
      get: "GET",
      post: "POST",
      put: "PUT",
      delete: "DELETE"
    };
    this.API.interceptors.request.use(
      async function(config) {
        const currentTime = new Date();
        if (!spinnerService.isShowing("mySpinner"))
          spinnerService.show("mySpinner");
        return config;
      },
      function(error) {
        if (spinnerService.isShowing("mySpinner"))
          spinnerService.hide("mySpinner");
        return Promise.reject(error);
      }
    );

    this.API.interceptors.response.use(
      function(response) {
        if (spinnerService.isShowing("mySpinner"))
          spinnerService.hide("mySpinner");
        return response;
      },
      function axiosRetryInterceptor(error) {
        
        var config = error.config;

        if(!config || !config.retry) {
          if (spinnerService.isShowing("mySpinner"))
            spinnerService.hide("mySpinner");
          return Promise.reject(error);
        }

        // Set the variable for keeping track of the retry count
        config.__retryCount = config.__retryCount || 0;

        // Check if we've maxed out the total number of retries
        if(config.__retryCount >= config.retry) {
          if (spinnerService.isShowing("mySpinner"))
            spinnerService.hide("mySpinner");
          return Promise.reject(error);
        }

        config.__retryCount += 1;
        var backoff = new Promise(function(resolve) {
            setTimeout(function() {
                resolve();
            }, config.retryDelay || 1);
        });
        
        // Return the promise in which recalls axios to retry the request
        return backoff.then(function() {
          if (spinnerService.isShowing("mySpinner"))
            spinnerService.hide("mySpinner");
          return axios(config);
        });
      });

    this.config = {
      headers: {
        "Content-Type": "application/json",
        "Cache-Control": "no-cache"
      }
    };
    this.paans = {
      headers: {
        Authorization:
          "Basic " + btoa(config.paans.appId + ":" + config.paans.secret),
        "Content-Type": "application/json"
      }
    };
  }

  handleApiErrors = response => {
    response.data = response.data || {};
    if (response.status !== 200) throw Error(response.statusText);
    if (response.data.message === TIMEOUT_LIMIT) {
      history.push("/timeout");
    }
    return response;
  };
  handleApiCall = (httpMethod, apiUrl, data, additionalHeaders) => {
    this.config = {
      retry: 3, 
      retryDelay: 1000,
      headers: {
        "Content-Type": "application/json",
        "Cache-Control": "no-cache",        
        actionKey: additionalHeaders ? additionalHeaders.actionKey || "" : "",
        decryptKey: additionalHeaders ? additionalHeaders.decryptKey || "" : ""
      }
    };
    switch (httpMethod) {
      case this.httpMethods.get:
        return this.API.get(apiUrl, this.config)
          .then(this.handleApiErrors)
          .then(response => ({ response }))
          .catch(error => ({ error }));
      case this.httpMethods.post:
        return this.API.post(apiUrl, data, this.config)
          .then(this.handleApiErrors)
          .then(response => ({ response }))
          .catch(error => ({ error }));
      case this.httpMethods.put:
        return this.API.put(apiUrl, data, this.config)
          .then(this.handleApiErrors)
          .then(response => ({ response }))
          .catch(error => ({ error }));
      case this.httpMethods.delete:
        return this.API.delete(apiUrl, data, this.config)
          .then(this.handleApiErrors)
          .then(response => ({ response }))
          .catch(error => ({ error }));
      default:
        return this.API.get(apiUrl, data, this.config)
          .then(this.handleApiErrors)
          .then(response => ({ response }))
          .catch(error => ({ error }));
    }
  };
  handlePaansCall = (apiUrl, data) => {
    return this.PAANS_API.post(apiUrl, data, this.paans)
      .then(this.handleApiErrors)
      .then(response => ({ response }))
      .catch(error => ({ error }));
  };
}

export class MLPUBLICAPI extends Api {
  constructor() {
    super();
    this.endPoints = {
      securityUrl: "Security",
      actionUrl: "action",
      enumUrl: "enum",
      linksUrl: "links",
      goUrl: "go",
      settingsUrl: "Settings"
    };
    this.action = {
      getSettings: link => {
        const header = {
          actionKey: link
        };
        return this.handleApiCall(
          this.httpMethods.get,
          `${this.endPoints.settingsUrl}/Languages?link=${link}`,
          null,
          header
        );
      },
      isConfirmationCompleted: link => {
        const header = {
          actionKey: link
        };
        return this.handleApiCall(
          this.httpMethods.get,
          `${this.endPoints.actionUrl}/${link}/completed`,
          null,
          header
        );
      },
      isRequestActive: link => {
        const header = {
          actionKey: link
        };
        return this.handleApiCall(
          this.httpMethods.get,
          `${this.endPoints.actionUrl}/${link}/requestActive`,
          null,
          header
        );
      },
      validateAction: ActionValidateRequest => {
        const header = {
          actionKey: ActionValidateRequest.actionKey,
          decryptKey: ActionValidateRequest.decryptKey
        };
        return this.handleApiCall(
          this.httpMethods.post,
          `${this.endPoints.actionUrl}/GenerateAccessCode`,
          null,
          header
        ).then(response => {
          return new Promise(function(resolve, reject) {
            resolve(response);
          });
        });
      },
      validatePaans: PaansValidateRequest => {
        const PaansCheckRequest = {
          appId: config.paans.appId,
          policyType: config.paans.policyType,
          region: config.paans.region,
          language: config.paans.language,
          userId: PaansValidateRequest.decryptKey,
          acceptUrl:
            config.paans.acceptUrl + "/" + PaansValidateRequest.actionKey + "/" + PaansValidateRequest.decryptKey + "/" + PaansValidateRequest.sessionId + "/" + PaansValidateRequest.sessionHash,
          rejectUrl:
            config.paans.rejectUrl + "/" + PaansValidateRequest.actionKey
        };

        return this.handlePaansCall(
          config.paans.apiMethod,
          PaansCheckRequest
        ).then(response => {
          return new Promise(function(resolve, reject) {
            resolve(response);
          });
        });
      },
      validatePassCode: PassCodeValidateRequest => {
        const header = {
          actionKey: PassCodeValidateRequest.actionKey,
          decryptKey: PassCodeValidateRequest.decryptKey
        };
        return this.handleApiCall(
          this.httpMethods.post,
          `${this.endPoints.actionUrl}/VerifyAccessCode`,
          PassCodeValidateRequest.accessCode,
          header
        ).then(response => {
          return new Promise(function(resolve, reject) {
            resolve(response);
          });
        });
      },
      getActionData: actionKey => {
        const header = {
          actionKey: actionKey
        };
        return this.handleApiCall(
          this.httpMethods.get,
          `${this.endPoints.actionUrl}/details`,
          null,
          header
        );
      },
      getActionDetails: actionId => {
        const header = {
          actionKey: actionId
        };
        return this.handleApiCall(
          this.httpMethods.get,
          `${this.endPoints.actionUrl}/${actionId}/details`,
          null,
          header
        );
      },
      expireOnTime: actionKey => {
        const header = {
          actionKey: actionKey
        };
        return this.handleApiCall(
          this.httpMethods.post,
          `${this.endPoints.linksUrl}/${actionKey}/expireoonetime`,
          null,
          header
        );
      },
      sendPassCode: ActionValidateRequest => {
        const header = {
          actionKey: ActionValidateRequest.actionKey,
          decryptKey: ActionValidateRequest.decryptKey
        };
        return this.handleApiCall(
          this.httpMethods.post,
          `${this.endPoints.actionUrl}/GenerateAccessCode`,
          ActionValidateRequest,
          header
        ).then(response => {
          return new Promise(function(resolve, reject) {
            resolve(response);
          });
        });
      }
    };
    this.enum = {
      getActionTypes: () => {
        return this.handleApiCall(
          this.httpMethods.get,
          `${this.endPoints.enumUrl}/actiontypes`
        );
      },
      getActionValidations: () => {
        return this.handleApiCall(
          this.httpMethods.get,
          `${this.endPoints.enumUrl}/actionvalidationtypes`
        );
      }
    };
    this.links = {
      expireLink: linkId =>
        this.handleApiCall(
          this.httpMethods.post,
          `${this.endPoints.linksUrl}/${linkId}/expire`
        )
    };
    this.goLink = {
      getGoLink: linkId =>
        this.handleApiCall(
          this.httpMethods.get,
          `${this.endPoints.goUrl}/${linkId}`
        )
    };
  }
}
